import React, { useEffect } from 'react';
import { handlePrint } from '../utils/printUtils';
import './RotcForm.css';

function RotcForm({ studentData }) {
  // Add debugging to see what data is coming in
  useEffect(() => {
    console.log('RotcForm received studentData:', studentData);
  }, [studentData]);

  // Check if studentData is null, undefined, or empty
  if (!studentData) {
    console.error('RotcForm: studentData is missing');
    return <div className="rotc-form-error">No student data available for ROTC form.</div>;
  }
  
  // Helper function to safely get values with fallbacks
  const getValue = (key, fallback = '') => {
    try {
      return studentData[key] || fallback;
    } catch (e) {
      console.error(`Error accessing ${key}:`, e);
      return fallback;
    }
  };
  
  // Format birthdate if it exists
  const formatBirthdate = (birthdate) => {
    if (!birthdate) return '';
    
    try {
      const date = new Date(birthdate);
      if (isNaN(date.getTime())) return birthdate;
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      console.error('Error formatting birthdate:', e);
      return birthdate;
    }
  };
  
  // Local print handler that ensures this specific form is printed
  const printThisForm = () => {
    console.log('Print button clicked in RotcForm component');
    
    try {
      // Get this specific form element
      const form = document.querySelector('.rotc-form');
      if (form) {
        console.log('Form found, preparing for print');
        // Add a data attribute to help identify this form for printing
        form.setAttribute('data-print-target', 'true');
      } else {
        console.error('Could not find form element to print');
      }
      
      // Call the handlePrint utility
      handlePrint();
      
      // Clean up after printing
      setTimeout(() => {
        if (form) {
          form.removeAttribute('data-print-target');
        }
      }, 2000);
    } catch (error) {
      console.error('Error in printThisForm:', error);
      // Fallback to default print
      window.print();
    }
  };

  return (
    <div className="rotc-form">
      <div className="rotc-form-header">
        <div className="form-note">
          Fill up this form, save and send as an e-mail attachment to <a href="mailto:reservist@army.mil.ph">reservist@army.mil.ph</a>.<br />
          You can also print and submit a copy of this form to your nearest RESCOM unit.
        </div>
        <h2>PHILIPPINE ARMY</h2>
        <h3>RESERVIST INFORMATION DATA SHEET</h3>
      </div>
      
      {/* RESERVIST PERSONAL INFORMATION */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="6" className="section-header">RESERVIST PERSONAL INFORMATION</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Rank</td>
            <td>Last Name</td>
            <td>First Name</td>
            <td>Middle Name</td>
            <td>Suffix</td>
            <td>Branch of Service</td>
          </tr>
          <tr>
            <td></td>
            <td>Arguelles</td>
            <td>Aia Noel</td>
            <td>Pagulong</td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td>AFPOS/MOS</td>
            <td>Source of Enlistment</td>
            <td colSpan="3">Authority</td>
            <td>Date of Enlistment</td>
          </tr>
          <tr>
            <td></td>
            <td></td>
            <td colSpan="3"></td>
            <td></td>
          </tr>
          <tr>
            <td>Serial Svcno</td>
            <td>Date of Last Promotion</td>
            <td>Authority</td>
            <td>Reserve Status</td>
            <td colSpan="2">Mobilization Center</td>
          </tr>
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td colSpan="2"></td>
          </tr>
          <tr>
            <td>Designation</td>
            <td>Special/Talent/Section</td>
            <td>Position</td>
            <td>Company</td>
            <td colSpan="2">Reserve Unit</td>
          </tr>
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td colSpan="2"></td>
          </tr>
          <tr>
            <td>Present Occupation</td>
            <td colSpan="4">Office Address</td>
            <td>Office Tel Nr</td>
          </tr>
          <tr>
            <td></td>
            <td colSpan="4">Brgy. Sablogon, Passi City, Iloilo</td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* PERSONAL IDENTIFICATION */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="4" className="section-header">PERSONAL IDENTIFICATION</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="form-label">Height (cm)</div>
              <div className="form-value">{getValue('height')}</div>
            </td>
            <td>
              <div className="form-label">Weight (kg)</div>
              <div className="form-value">{getValue('weight')}</div>
            </td>
            <td>
              <div className="form-label">Identifying Marks</div>
              <div className="form-value">{getValue('identifyingMarks')}</div>
            </td>
            <td>
              <div className="form-label">TIN No.</div>
              <div className="form-value">{getValue('tinNo')}</div>
            </td>
          </tr>
          <tr>
            <td>
              <div className="form-label">SSS/GSIS No.</div>
              <div className="form-value">{getValue('sssNo')}</div>
            </td>
            <td>
              <div className="form-label">PhilHealth No.</div>
              <div className="form-value">{getValue('philhealthNo')}</div>
            </td>
            <td>
              <div className="form-label">Pag-IBIG No.</div>
              <div className="form-value">{getValue('pagibigNo')}</div>
            </td>
            <td>
              <div className="form-label">Student ID</div>
              <div className="form-value">{getValue('studentId')}</div>
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* VACCINATION RECORDS */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="5" className="section-header">VACCINATION RECORDS</th>
          </tr>
          <tr className="sub-header">
            <th>Vacci Name</th>
            <th>Date of Vaccination</th>
            <th>Vaccine Type/Brand</th>
            <th>Batch Manufacturer</th>
            <th>Country of Origin</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* AFPSAT QUALIFICATION */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="2" className="section-header">AFPSAT QUALIFICATION</th>
          </tr>
          <tr className="sub-header">
            <th>Date of Exam</th>
            <th>Rating</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* MILITARY SCHOOLS/TRAINING */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="3" className="section-header">MILITARY SCHOOLS/TRAINING</th>
          </tr>
          <tr className="sub-header">
            <th>Course/Schooling</th>
            <th>School</th>
            <th>Date Completed</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* AWARDS AND DECORATION */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="3" className="section-header">AWARDS AND DECORATION</th>
          </tr>
          <tr className="sub-header">
            <th>Award/Citation</th>
            <th>Authority</th>
            <th>Date Awarded</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* DEPENDENTS */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="5" className="section-header">DEPENDENTS</th>
          </tr>
          <tr className="sub-header">
            <th>Name</th>
            <th>Relation</th>
            <th>Address</th>
            <th>Contact No.</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{getValue('emergencyContact') || 'Aia Noel P Arguelles'}</td>
            <td>Emergency Contact</td>
            <td></td>
            <td>{getValue('emergencyContactNo') || '09501597626'}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* EDUCATIONAL BACKGROUND */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="3" className="section-header">EDUCATIONAL BACKGROUND (TERTIARY LEVEL)</th>
          </tr>
          <tr className="sub-header">
            <th>School</th>
            <th>Course</th>
            <th>Date Graduated</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{getValue('school')}</td>
            <td>{getValue('course') || 'BSBA'}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* UNIT ASSIGNMENT */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="4" className="section-header">UNIT ASSIGNMENT</th>
          </tr>
          <tr className="sub-header">
            <th>Unit</th>
            <th>Address</th>
            <th>Date Start</th>
            <th>Date End</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
      
      {/* MILITARY ACTIVITIES */}
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan="3" className="section-header">MILITARY ACTIVITIES</th>
          </tr>
          <tr className="sub-header">
            <th>Activity</th>
            <th>Date Start</th>
            <th>Date End</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default RotcForm; 